import dayjs from 'dayjs';

import api from './api';
import invoiceApi from './invoiceApi';

// Varsayılan fatura özeti için yardımcı fonksiyon
const getDefaultInvoiceSummary = () => {
  return {
    success: false,
    message: 'Fatura özeti alınamadı',
    data: {
      daily: {
        WS: { count: 0, total: 0 }, // Toptan Satış
        BP: { count: 0, total: 0 }, // Toptan Alış
        EP: { count: 0, total: 0 }  // Masraf
      },
      weekly: {
        WS: { count: 0, total: 0 },
        BP: { count: 0, total: 0 },
        EP: { count: 0, total: 0 }
      },
      monthly: {
        WS: { count: 0, total: 0 },
        BP: { count: 0, total: 0 },
        EP: { count: 0, total: 0 }
      }
    }
  };
};

// Fatura özeti için parametreler
export interface InvoiceSummaryParams {
  startDate?: string;
  endDate?: string;
  processCodeList?: string[];
}

// Fatura özet API'si
const invoiceSummaryApi = {
  // Toptan Satış Faturaları listesi
  getWholesaleInvoices: async (params: any): Promise<any> => {
    return invoiceApi.getWholesaleInvoices(params);
  },
  
  // Toptan Alış Faturaları listesi
  getWholesalePurchaseInvoices: async (params: any): Promise<any> => {
    return invoiceApi.getWholesalePurchaseInvoices(params);
  },
  
  // Masraf Faturaları listesi
  getExpenseInvoices: async (params: any): Promise<any> => {
    return invoiceApi.getExpenseInvoices(params);
  },
  // Fatura özetlerini getir (Günlük, Haftalık, Aylık)
  getInvoiceSummary: async (params: InvoiceSummaryParams): Promise<any> => {
    try {
      // Token kontrolü yap
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      if (!token) {
        return getDefaultInvoiceSummary();
      }
      
      // API endpoint'i henüz hazır olmadığı için hata fırlat
      // Bu sayede Dashboard'daki manuel hesaplama kısmı çalışacak
      throw new Error('API endpoint henüz hazır değil, manuel hesaplama kullanılacak');
      
      /* API endpoint hazır olduğunda bu kodu aktif et
      // Varsayılan olarak bugünün tarihini al
      const today = dayjs();
      
      // Parametreleri hazırla
      const apiParams = {
        startDate: params.startDate || today.format('YYYY-MM-DD'),
        endDate: params.endDate || today.format('YYYY-MM-DD'),
        processCodeList: params.processCodeList || ['WS', 'BP', 'EP'] // Tüm fatura tipleri
      };
      
      try {
        const response = await api.get('/api/v1/Invoice/summary', { params: apiParams });
        if (response?.data?.success) {
          return response.data;
        } else {
          return getDefaultInvoiceSummary();
        }
      } catch (apiError: any) {
        // API hatası durumunda varsayılan değerleri döndür
        // 400, 401, 404 gibi beklenen hatalar için log gösterme
        if (apiError?.response?.status !== 400 && apiError?.response?.status !== 401 && apiError?.response?.status !== 404) {
          console.error('Fatura özeti alınırken beklenmeyen hata:', apiError?.message || 'Bilinmeyen hata');
        }
        return getDefaultInvoiceSummary();
      }
      */
    } catch (error: any) {
      // Genel hata durumunda varsayılan değerleri döndür
      return getDefaultInvoiceSummary();
    }
  },
  
  // Mevcut API'ler olmadığı için frontend'de hesaplama yapacak metot
  calculateInvoiceSummary: async (): Promise<any> => {
    try {
      const today = dayjs();
      const startOfWeek = today.startOf('week').format('YYYY-MM-DD');
      const startOfMonth = today.startOf('month').format('YYYY-MM-DD');
      const endDate = today.format('YYYY-MM-DD');
      
      // Günlük, haftalık ve aylık veriler için API çağrıları
      const dailyParams = {
        startDate: endDate,
        endDate: endDate
      };
      
      const weeklyParams = {
        startDate: startOfWeek,
        endDate: endDate
      };
      
      const monthlyParams = {
        startDate: startOfMonth,
        endDate: endDate
      };
      
      // Toptan Satış faturaları
      const dailySales = await api.get('/api/v1/Invoice/wholesale', { params: { ...dailyParams, processCode: 'WS' } });
      const weeklySales = await api.get('/api/v1/Invoice/wholesale', { params: { ...weeklyParams, processCode: 'WS' } });
      const monthlySales = await api.get('/api/v1/Invoice/wholesale', { params: { ...monthlyParams, processCode: 'WS' } });
      
      // Toptan Alış faturaları
      const dailyPurchase = await api.get('/api/v1/Invoice/wholesale-purchase', { params: { ...dailyParams, processCode: 'BP' } });
      const weeklyPurchase = await api.get('/api/v1/Invoice/wholesale-purchase', { params: { ...weeklyParams, processCode: 'BP' } });
      const monthlyPurchase = await api.get('/api/v1/Invoice/wholesale-purchase', { params: { ...monthlyParams, processCode: 'BP' } });
      
      // Masraf faturaları
      const dailyExpense = await api.get('/api/v1/Invoice/expense', { params: { ...dailyParams, processCode: 'EP' } });
      const weeklyExpense = await api.get('/api/v1/Invoice/expense', { params: { ...weeklyParams, processCode: 'EP' } });
      const monthlyExpense = await api.get('/api/v1/Invoice/expense', { params: { ...monthlyParams, processCode: 'EP' } });
      
      // Toplamları hesapla
      const calculateTotal = (data: any) => {
        if (!data || !data.data || !data.data.items) return { count: 0, total: 0 };
        
        const items = data.data.items;
        const count = items.length;
        const total = items.reduce((sum: number, item: any) => sum + (item.netAmount || 0), 0);
        
        return { count, total };
      };
      
      // Sonuçları birleştir
      return {
        success: true,
        data: {
          daily: {
            WS: calculateTotal(dailySales),
            BP: calculateTotal(dailyPurchase),
            EP: calculateTotal(dailyExpense)
          },
          weekly: {
            WS: calculateTotal(weeklySales),
            BP: calculateTotal(weeklyPurchase),
            EP: calculateTotal(weeklyExpense)
          },
          monthly: {
            WS: calculateTotal(monthlySales),
            BP: calculateTotal(monthlyPurchase),
            EP: calculateTotal(monthlyExpense)
          }
        }
      };
    } catch (error: any) {
      // Sadece beklenmeyen hatalar için log göster
      if (error?.response?.status !== 400 && error?.response?.status !== 401 && error?.response?.status !== 404) {
        console.error('Fatura özeti hesaplanırken beklenmeyen hata:', error?.message || 'Bilinmeyen hata');
      }
      return getDefaultInvoiceSummary();
    }
  }
};

export default invoiceSummaryApi;
